import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import {
  GET_SESSION_STATUS_COUNT,
  GET_TEAM_MEMBER_SESSION_STATUS_COUNT,
} from '@/components/layout/Navigation/sessions/hooks/sessionStatusCountKeys';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useIsReviewingOtherUser, useQueryUser } from '@/hooks/query/useQueryUser';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useCanReviewOtherUsersSessionList } from '@/hooks/useCanReviewOtherUsersSessionList';
import {
  getSessionCountBySessionStatus,
  getTeamMemberSessionCountBySessionStatus,
} from '@/pages/api/scribeAPIV2';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

export interface UseQuerySessionCountBySessionStatusOptions {
  /** Gate the query independently of its own readiness checks. Defaults to
   *  on — the All Sessions scope passes `false` so its single whole-scope
   *  call (`useQueryAllSessionsStatusCounts`) replaces this per-status one
   *  instead of firing alongside it. */
  enabled?: boolean;
}

export function useQuerySessionCountBySessionStatus(
  status: SessionStatus,
  options: UseQuerySessionCountBySessionStatusOptions = {}
) {
  const { teamDetails, isSupportRole } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const hasAllowlistedSessionReviewAccess = useCanReviewOtherUsersSessionList();
  const canReviewOtherUsersSessionList =
    isSupportRole || hasAllowlistedSessionReviewAccess;
  const { data: kindeUserId } = useQueryUser((data) => data?.kinde_user_id);

  const teamMemberQuery = useQueryTeamMemberSessionCountBySessionStatus(status, options);
  const adminQuery = useQueryAdminSessionCountBySessionStatus(status, options);

  if (
    teamDetails?.team.id &&
    selectedReviewUser &&
    selectedReviewUser !== kindeUserId &&
    canReviewOtherUsersSessionList
  ) {
    return teamMemberQuery;
  } else {
    return adminQuery;
  }
}

function useQueryAdminSessionCountBySessionStatus(
  status: SessionStatus,
  options: UseQuerySessionCountBySessionStatusOptions
) {
  const token = useAuthToken();
  const { data: user } = useQueryUser((data) => data?.kinde_user_id);

  return useQuery({
    queryKey: [GET_SESSION_STATUS_COUNT, status],
    queryFn: async () => {
      const { data } = await getSessionCountBySessionStatus(status);
      return data;
    },
    enabled: Boolean(token && user && (options.enabled ?? true)),
  });
}

function useQueryTeamMemberSessionCountBySessionStatus(
  status: SessionStatus,
  options: UseQuerySessionCountBySessionStatusOptions
) {
  const { teamDetails, isSupportRole } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const hasAllowlistedSessionReviewAccess = useCanReviewOtherUsersSessionList();
  const canReviewOtherUsersSessionList =
    isSupportRole || hasAllowlistedSessionReviewAccess;
  const token = useAuthToken();
  const isReviewingOtherUser = useIsReviewingOtherUser();

  const teamId = teamDetails?.team.id;
  const memberId = selectedReviewUser;

  return useQuery({
    queryKey: [GET_TEAM_MEMBER_SESSION_STATUS_COUNT, status, { teamId }, { memberId }],
    queryFn: async () => {
      if (teamId && memberId) {
        const { data } = await getTeamMemberSessionCountBySessionStatus({
          teamId,
          memberId,
          sessionStatus: status,
        });
        return data;
      }
    },
    enabled: Boolean(
      token &&
      teamId &&
      memberId &&
      canReviewOtherUsersSessionList &&
      isReviewingOtherUser &&
      (options.enabled ?? true)
    ),
  });
}
